import React, { useEffect, useRef, useState } from "react";

const VideoBackground = ({
  videoSrcWebm = "",
  videoSrcMp4 = "",
  posterImage = "",
  onVideoEnd,
  onAlmostEnd,
  onVideoReady,
}) => {
  const videoRef = useRef(null);
  const almostEndTriggered = useRef(false);
  const hasPlayedOnce = useRef(false);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // Handler for video time updates
    const handleTimeUpdate = () => {
      // Check if the event should be triggered and hasn't been already
      if (onAlmostEnd && !almostEndTriggered.current && videoElement.duration) {
        const timeLeft = videoElement.duration - videoElement.currentTime;
        if (timeLeft <= 2) {
          onAlmostEnd();
          almostEndTriggered.current = true;
        }
      }
    };

    // Add the event listener
    videoElement.addEventListener("timeupdate", handleTimeUpdate);

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasPlayedOnce.current) {
          const vid = videoRef.current;
          if (vid) {
            hasPlayedOnce.current = true;
            vid.play().catch((error) => {
              console.error("Video play failed:", error);
            });
          }
        }
      },
      {
        threshold: 0.5,
      }
    );

    observer.observe(videoElement);

    return () => {
      // Cleanup: remove observer and event listener
      observer.unobserve(videoElement);
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [onAlmostEnd]); // Added dependency

  const handleVideoEnd = () => {
    if (onVideoEnd) {
      onVideoEnd();
    }
  };

  return (
    <div className="w-full h-full z-0 absolute inset-0 flex items-center justify-center">
      <video
        ref={videoRef}
        className="w-[100%] sm:w-[100%] md:w-[100%] lg:w-[100%] xl:w-[100%] 2xl:w-[75%] h-[60%] sm:h-[65%] md:h-[70%] lg:h-[72%] xl:h-[74%] 2xl:h-[75%] object-cover object-center rounded-lg transform translate-y-12 sm:translate-y-12 md:translate-y-4"
        muted
        playsInline
        poster={posterImage}
        onEnded={handleVideoEnd}
        onCanPlayThrough={onVideoReady}
      >
        {/* WebM is preferred for better compression and quality */}
        {videoSrcWebm && <source src={videoSrcWebm} type="video/webm" />}
        {/* MP4 fallback for browsers that don't support WebM */}
        {videoSrcMp4 && <source src={videoSrcMp4} type="video/mp4" />}
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

export default VideoBackground;
